import { Transaction } from '../types/transaction';

interface Streak {
  merchant: string;
  length: number;
  startDate: string;
  endDate: string;
  isActive: boolean;
}

export const getTopStreaks = (transactions: Transaction[]): Streak[] => {
  if (!transactions || transactions.length === 0) {
    return [];
  }

  // Sort transactions by date in descending order
  const sortedTransactions = [...transactions].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  // Group transactions by merchant
  const merchantTransactions: { [key: string]: Transaction[] } = {};
  sortedTransactions.forEach(transaction => {
    if (!merchantTransactions[transaction.merchant]) {
      merchantTransactions[transaction.merchant] = [];
    }
    // Remove duplicate transactions on the same day
    const existingTransaction = merchantTransactions[transaction.merchant].find(
      t => new Date(t.date).toDateString() === new Date(transaction.date).toDateString()
    );
    if (!existingTransaction) {
      merchantTransactions[transaction.merchant].push(transaction);
    }
  });

  // Calculate streaks for each merchant
  const streaks: Streak[] = [];
  const now = new Date();
  
  Object.entries(merchantTransactions).forEach(([merchant, txns]) => {
    if (txns.length < 2) return; // Skip if less than 2 transactions

    let currentStreak = 1;
    let maxStreak = 1;
    let lastDate = new Date(txns[0].date);
    let maxStreakStartDate = txns[0].date;
    let maxStreakEndDate = txns[0].date;
    let currentStreakStartDate = txns[0].date;
    
    for (let i = 1; i < txns.length; i++) {
      const currentDate = new Date(txns[i].date);
      const dayDiff = Math.floor(
        (lastDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (dayDiff === 1) {
        currentStreak++;
        if (currentStreak > maxStreak) {
          maxStreak = currentStreak;
          maxStreakEndDate = txns[i].date;
          maxStreakStartDate = currentStreakStartDate;
        }
      } else {
        currentStreak = 1;
        currentStreakStartDate = txns[i].date;
      }
      
      lastDate = currentDate;
    }

    // Check if streak is active (transaction within last 24 hours)
    const lastTxnDate = new Date(txns[txns.length - 1].date);
    const isActive = (now.getTime() - lastTxnDate.getTime()) <= (24 * 60 * 60 * 1000);

    if (maxStreak >= 2) { // Only include streaks of 2 or more days
      streaks.push({
        merchant,
        length: maxStreak,
        startDate: maxStreakStartDate,
        endDate: maxStreakEndDate,
        isActive
      });
    }
  });

  // Sort by streak length and return top 3
  return streaks
    .sort((a, b) => b.length - a.length)
    .slice(0, 3);
};
